import React, { useEffect } from "react";
import { Button, styled, useMediaQuery, useTheme } from "@mui/material";
import { fontSizes } from "../../../theme/font-sizes";

const StyledButton = styled(Button)(({ theme, backgroundImage }) => ({
  fontSize: fontSizes.superHeading4,
  fontFamily: "MontserratBold",
  borderRadius: "12px",
  textTransform: "none",
  position: "relative",
  overflow: "hidden",
  color: "white",
  zIndex: 2,
  background: "linear-gradient(135deg, #6C1717 100%, #EE8005)",
  boxShadow: "-2px 0px 14px rgba(199, 183, 40, 0.27)",

  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: -2,
    backgroundImage: `url(${backgroundImage})`,
    backgroundSize: "cover",
    backgroundPosition: "right",
  },

  "&:hover": {
    background: "linear-gradient(135deg, #FD8804, #8E5009)",
    boxShadow: "-2px 5px 32px rgba(199, 183, 40, 0.47)",
  },
}));

const TextContainer = styled("div")({
  position: "absolute",
  top: "50%",
  left: "55%",
  transform: "translate(-50%, -50%)",
  zIndex: 1,
  textAlign: "left",
  width: "100%",
  lineHeight: 1.2,
  fontStyle: "italic",
});

const GalaxyContainer = styled("div")({
  position: "absolute",
  inset: 0,
  opacity: 0,
  transition: "opacity 0.25s",
  mask: "radial-gradient(white, transparent)",
});

const Galaxy = styled("div")({
  position: "absolute",
  width: "100%",
  aspectRatio: "1",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  overflow: "hidden",
  opacity: 0,
  transition: "opacity 0.3s",
});

const GalaxyRing = styled("div")({
  height: "200%",
  width: "200%",
  position: "absolute",
  top: "50%",
  left: "50%",
  borderRadius: "50%",
  transform:
    "translate(-28%, -40%) rotateX(-24deg) rotateY(-30deg) rotateX(90deg)",
  transformStyle: "preserve-3d",
});

const Star = styled("span")({
  position: "absolute",
  backgroundColor: "white",
  borderRadius: "50%",
  opacity: "var(--alpha, 0.7)",
  top: "50%",
  left: "30%",
  width: "calc(var(--size, 4) * 1px)",
  height: "calc(var(--size, 4) * 1px)",
  transform:
    "translate(-50%, -50%) rotate(10deg) rotate(0deg) translateY(calc(var(--distance, 50) * 1px))",
  animation:
    "orbit calc(var(--duration, 10) * 1s) calc(var(--delay, 0) * -1s) infinite linear",
  "@keyframes orbit": {
    to: {
      transform:
        "translate(-50%, -50%) rotate(10deg) rotate(360deg) translateY(calc(var(--distance, 50) * 1px))",
    },
  },
});

export const SparkContainer = styled("div")({
  position: "absolute",
  inset: 0,
  borderRadius: "12px",
  overflow: "hidden",
  pointerEvents: "none",
  zIndex: 1,
  opacity: 0,
  transition: "opacity 0.3s",
  "&.active": {
    opacity: 1,
  },
  "& .spark-top, & .spark-bottom": {
    position: "absolute",
    left: 0,
    right: 0,
    height: "4px",
    background: "linear-gradient(90deg, transparent, white, transparent)",
    opacity: 0,
  },
  "& .spark-top": {
    top: 0,
    animationName: "moveSparkTop, showTopSpark",
    animationDuration: "1.5s, 1.5s",
    animationTimingFunction: "linear, linear",
    animationIterationCount: "infinite, infinite",
  },
  "& .spark-bottom": {
    bottom: 0,
    animationName: "moveSparkBottom, showBottomSpark",
    animationDuration: "1.5s, 1.5s",
    animationTimingFunction: "linear, linear",
    animationIterationCount: "infinite, infinite",
    animationDelay: "1.5s, 1.5s",
  },
  "@keyframes moveSparkTop": {
    "0%": { transform: "translateX(-100%)" },
    "100%": { transform: "translateX(100%)" },
  },
  "@keyframes moveSparkBottom": {
    "0%": { transform: "translateX(100%)" },
    "100%": { transform: "translateX(-100%)" },
  },
  "@keyframes showTopSpark": {
    "0%": { opacity: 1 },
    "50%": { opacity: 1 },
    "100%": { opacity: 0 },
  },
  "@keyframes showBottomSpark": {
    "0%": { opacity: 1 },
    "50%": { opacity: 1 },
    "100%": { opacity: 0 },
  },
});

const FindMatchButton = ({
  text,
  py = 2,
  width = "auto",
  backgroundImage,
  onClick,
  ...props
}) => {
  const theme = useTheme();

  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    const stars = document.querySelectorAll(".star");
    stars.forEach((star) => {
      star.style.setProperty(
        "--angle",
        `${Math.floor(Math.random() * 361)}deg`
      );
      star.style.setProperty(
        "--duration",
        `${Math.floor(Math.random() * 15) + 6}`
      );
      star.style.setProperty(
        "--delay",
        `${Math.floor(Math.random() * 10) + 1}`
      );
      star.style.setProperty(
        "--alpha",
        `${(Math.floor(Math.random() * 51) + 40) / 100}`
      );
      star.style.setProperty("--size", `${Math.floor(Math.random() * 5) + 2}`);
      star.style.setProperty(
        "--distance",
        `${Math.floor(Math.random() * 161) + 40}`
      );
    });
  }, []);

  return (
    <StyledButton
      variant="contained"
      onClick={onClick}
      sx={{
        py: py,
        width: isSmallScreen ? "94%" : width,
        "&:hover .galaxy-container, &:hover .galaxy": {
          opacity: 1,
        },
        "&:hover .spark-container": {
          opacity: 1,
        },
      }}
      backgroundImage={backgroundImage}
      {...props}
    >
      <SparkContainer className="spark-container">
        <div className="spark-top" />
        <div className="spark-bottom" />
      </SparkContainer>
      <TextContainer>{text}</TextContainer>
      <GalaxyContainer className="galaxy-container">
        <Star className="star" />
        <Star className="star" />
        <Star className="star" />
        <Star className="star" />
      </GalaxyContainer>
      <Galaxy className="galaxy">
        <GalaxyRing>
          {[...Array(20)].map((_, index) => (
            <Star key={index} className="star" />
          ))}
        </GalaxyRing>
      </Galaxy>
    </StyledButton>
  );
};

export default FindMatchButton;
